// Analytics and Feature Toggle
import { GrowthBook } from "@growthbook/growthbook";
import { getCountryCodeFromDomain } from "./country-code-utils";
import axios from "axios";
import { isEmpty } from "lodash";
import mixpanel from "mixpanel-browser";

export const FEATURE_KEYS = {
  ADDITIONAL_BANK_FEE: "new_payment_method_ui",
  NEW_SBA_BANNER: "sa-27948_new_sba_banner",
  TOGGLE_SBA_PAGE: "sa-27946_toggle_sba_page",
  SKIP_NAME_CHECK: "sa-27951_skip_name_check",
  SKIP_BRAINSTORMING_NAME: "sa-27950_skip_brainstorming_name",
  MANDATORY_PHONE: "sa-28318_mandatory_phone",
  AUTO_APPLY_COUPON: "sa-27945_auto_apply_coupon",
  SKIP_VISA_PAGE: "sa-28317_skip_visa_page",
  DISABLE_NAME_CHECKER_PAGE: "sa-28316_disable_name_checker_page",
  GOOGLE_RATING: "sa-28677-google-rating",
  SAVE_FOR_LATER: "sa-30539_save_for_later_config",
  NEED_GUIDANCE_POPUP_BANNER: "sa-30724_need_guidance_pop_up_banner",
  ACCOUNTING_AU_OPTIONS: "sa-31723_accounting_au_options",
  UNIQUE_PROMO_BANNER_TEXT: "sa-31938_unique_promo_banner_text",
  REDIRECT_FROM_ALPHA_FLOW: "sa-30996_redirect_user_from_alpha_flow",
  SUPPORT_NUMBER: "sa-32637_support_phone_number",
  // Temporary key for development
  MEETING_URL: "sa-31491_meeting_url_1",
  DATE_INCORPORATION_PAYMENT_COMPLETION_PAGE: "sa-33548_date_incorporation_in_payment_completion",
  DISABLE_LET_DO_IT_BUTTON: "sa-33853_disable_let_do_it_button",
  POLICY_AND_TERMS_URLS: "sa-34229_privacy_policy_and_terms_urls",
};

const getFallbackGrowthbookFeatureAsync = async (fallbackJsonUrl) => {
  try {
    const response = await axios.get(fallbackJsonUrl);
    const featureData = response.data;

    return featureData;
  } catch (e) {
    console.error("Failed to get Growthbook fallback values", e);
    return {};
  }
};

class GrowthbookUtil {
  instance = null;

  async init(growthbookToken, growthbookHost, fallbackJsonUrl, isProd = true) {
    // User attributes
    let clientId;

    const paramsString = window.location.search;
    const searchParams = new URLSearchParams(paramsString);
    const countryCode = await getCountryCodeFromDomain();

    const attributes = {
      userId: null,
      country: countryCode,
      bundle: searchParams.get("bundle"),
      url: window.location.hostname,
    };
    try {
      clientId = document.cookie
        .match(/_ga=(.+?);/)[1]
        .split(".")
        .slice(-2)
        .join(".");
      attributes.clientId = clientId;
    } catch {
      clientId = null;
    }

    this.instance = new GrowthBook({
      apiHost: growthbookHost,
      clientKey: growthbookToken,
      // Enable easier debugging during development and reproduction on PROD
      // We might want to disable on PROD, or enable it differently
      enableDevMode: isProd !== true || localStorage.getItem("gbDevMode") !== null,
      backgroundSync: false,
      attributes,
      // Only required for A/B testing
      trackingCallback: function (experiment, result) {
        mixpanel.track("$experiment_started", {
          "Experiment name": experiment.key,
          "Variant name": result.variationId,
          $source: "growthbook",
        });
      },
    });
    await this.instance.loadFeatures();

    if (isEmpty(this.instance.context.features)) {
      const fallbackFeatureData = await getFallbackGrowthbookFeatureAsync(fallbackJsonUrl);
      await this.instance.setFeatures(fallbackFeatureData.features);
    }

    return this.instance;
  }

  isFeatureOn(featureKey) {
    if (!this.instance) return false;

    return this.instance.isOn(featureKey);
  }

  getFeatureValue(featureKey, fallbackValue) {
    return this.instance.getFeatureValue(featureKey, fallbackValue);
  }

  setUserAttributes(attributes) {
    if (!this.instance) return;
    this.instance.setAttributes({
      ...this.instance.getAttributes(),
      ...attributes,
    });
  }
}

export const growthbookUtil = new GrowthbookUtil();
